import { FaLock } from "react-icons/fa";
import { FaCoins } from "react-icons/fa6";

import "./CharacterOptionButton.css";
import { useState } from "react";
import { useEffect } from "react";
import { Rect } from "@mythic/pixl";

interface Props {
    file?: string;
    owned?: boolean;
    isCurrent?: boolean;
    price?: number;
    iconFrame?: Rect;
    onClick?: () => void;
}

export function CharacterOptionButton({
    file,
    owned,
    isCurrent,
    price,
    iconFrame,
    onClick,
}: Props) {
    const [backImage, setBackImage] = useState<string>("");

    function formatNumber(value: number) {
        const format = (num: number, suffix: string) => {
            const fixed = num.toFixed(2);
            const trimmed = fixed.endsWith(".00")
                ? Math.floor(num).toString()
                : fixed.replace(/\.?0+$/, ""); // remove trailing .0 or .00
            return trimmed + suffix;
        };

        if (value < 10000) return value.toString();
        if (value < 1_000_000) return format(value / 1000, "k");
        if (value < 1_000_000_000) return format(value / 1_000_000, "m");
        return format(value / 1_000_000_000, "b");
    }

    function cropImage(image: HTMLImageElement, { x, y, width, height }: Rect) {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        if (!ctx) return image.src;
        ctx.drawImage(
            image,

            x,
            y,
            width,
            height, // Source rect
            0,
            0,
            width,
            height, // Destination on canvas
        );

        return canvas.toDataURL("image/png"); // Returns a base64 data URL
    }

    useEffect(() => {
        if (!iconFrame || !file) return;

        const image = new Image();
        image.crossOrigin = "anonymous";
        image.src = `${import.meta.env.VITE_SERVER_URL || ""}${file}`;

        image.onload = () => {
            const croppedDataUrl = cropImage(image, iconFrame); // x, y, width, height
            setBackImage(croppedDataUrl);
        };
    }, [file, iconFrame]);

    // Create a button, taking into account if the item is
    // 1. Free or Premium
    // 2. If premium, is it owned?
    return (
        <div className="tooltip-wrapper">
            <button
                className={`character-option-button${
                    isCurrent ? " selected" : ""
                }`}
                style={
                    file
                        ? {
                              backgroundImage: `url(${backImage})`,
                          }
                        : {}
                }
                onClick={onClick}
            >
                {(price ?? 0) > 0 && !owned && (
                    <>
                        {
                            // The following div and FaLock are only displayed
                            // if the item is premium and the user does not own
                            // the item.
                        }
                        <div
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                backgroundColor: "rgba(0, 0, 0, 0.5)", // dark overlay
                                zIndex: 1,
                                borderRadius: "8px",
                            }}
                        />
                        <FaLock
                            style={{
                                position: "absolute",
                                top: "4px",
                                right: "4px",
                                color: "var(--accent-b)",
                                zIndex: 2,
                            }}
                        />
                    </>
                )}
            </button>

            {
                // If the item is premium and not owned, create a tooltip
                // for the item that will pop up beneath the item and show
                // the cost of the item so users don't have to click everything
                // just to see the price.
            }
            {(price ?? 0) > 0 && !owned && (
                <div className="tooltip-hider">
                    <div className="tooltip">
                        <span className="tooltip-body">
                            <FaCoins /> {formatNumber(price ?? 0)}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}
